import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Drawer, Popover, Select, Table, message } from "antd";
import { Check, Info, RefreshCw } from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import { applyDiscoveredPolicies, discoverADPolicies } from "../../../service/adPolicyService";
import { ClassBadge } from "./adPolicyTokens";
import { ACTIONS, normalizeAction } from "./adPolicyModel";

const ACTION_OPTIONS = ACTIONS.map((a) => ({ value: a.value, label: a.label }));

const ACTION_COLOR = {
  mfa_required: "var(--an-danger)",
  allow: "var(--an-success)",
  block: "var(--an-dangerFg)",
};

// DiscoverADPolicies caps members[] at 50 per group, so the preview is a
// sample — never the full roster, even when memberCount is small enough to
// look complete.
const MEMBER_SAMPLE_CAP = 50;

export default function DiscoverFromADDrawer({ open, onClose, domains, onApplied }) {
  const [messageApi, messageContext] = message.useMessage();
  const [adId, setAdId] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [rowState, setRowState] = useState({}); // { [adGroupId]: { checked, action } }

  // Reset when drawer opens; preselect first domain
  useEffect(() => {
    if (open) {
      setAdId(domains?.[0]?.id ?? null);
      setSuggestions([]);
      setRowState({});
    }
  }, [open, domains]);

  const runScan = useCallback(
    (overrideAdId) => {
      const id = overrideAdId ?? adId;
      if (!id) {
        messageApi.warning("Pick an AD domain first");
        return;
      }
      setScanLoading(true);
      setSuggestions([]);
      setRowState({});
      discoverADPolicies({
        payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), adId: id },
        onSuccess: (res) => {
          const list = res?.suggestions || [];
          setSuggestions(list);
          const initial = {};
          list.forEach((s) => {
            initial[s.adGroupId] = {
              // Groups that already have a policy are never pre-selected —
              // applying again would stack a second policy on the same group.
              checked:
                !s.policyExists &&
                (s.classification === "privileged" || s.classification === "standard"),
              action: s.recommendedAction
                ? normalizeAction(s.recommendedAction)
                : "mfa_required",
            };
          });
          setRowState(initial);
          setScanLoading(false);
        },
        onFailure: () => {
          messageApi.error("Failed to discover policies");
          setScanLoading(false);
        },
      });
    },
    [adId, messageApi],
  );

  const handleDomainChange = (val) => {
    setAdId(val);
    if (val) runScan(val);
  };

  const selectedCount = useMemo(
    () =>
      suggestions.filter(
        (s) => rowState[s.adGroupId]?.checked && !s.policyExists,
      ).length,
    [suggestions, rowState],
  );

  const updateRow = (id, patch) =>
    setRowState((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const toggleAll = (checked) => {
    const next = { ...rowState };
    suggestions.forEach((s) => {
      if (!s.policyExists) next[s.adGroupId] = { ...next[s.adGroupId], checked };
    });
    setRowState(next);
  };

  const allSelectable = suggestions.filter((s) => !s.policyExists);
  const allChecked =
    allSelectable.length > 0 &&
    allSelectable.every((s) => rowState[s.adGroupId]?.checked);
  const someChecked =
    !allChecked && allSelectable.some((s) => rowState[s.adGroupId]?.checked);

  const handleApply = () => {
    const selections = suggestions
      .filter((s) => rowState[s.adGroupId]?.checked && !s.policyExists)
      .map((s) => ({
        adGroupId: s.adGroupId,
        groupName: s.groupName,
        action: rowState[s.adGroupId].action,
      }));
    if (selections.length === 0) {
      messageApi.warning("Select at least one group to apply");
      return;
    }
    setApplyLoading(true);
    applyDiscoveredPolicies({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        adId,
        selections,
        appliedBy: localStorage.getItem("userId") || "",
      },
      onSuccess: () => {
        messageApi.success(`${selections.length} ${selections.length === 1 ? "policy" : "policies"} created`);
        setApplyLoading(false);
        onApplied && onApplied();
      },
      onFailure: () => {
        messageApi.error("Failed to apply selected policies");
        setApplyLoading(false);
      },
    });
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={allChecked}
          indeterminate={someChecked}
          onChange={(e) => toggleAll(e.target.checked)}
        />
      ),
      key: "select",
      width: 38,
      render: (_, row) => (
        <Checkbox
          checked={!!rowState[row.adGroupId]?.checked && !row.policyExists}
          disabled={row.policyExists}
          onChange={(e) => updateRow(row.adGroupId, { checked: e.target.checked })}
        />
      ),
    },
    {
      title: "Group",
      key: "group",
      render: (_, row) => (
        <span
          style={{
            fontWeight: 500,
            color: row.policyExists ? AN_COLORS.muted : AN_COLORS.ink,
            fontFamily: AN_FONT,
          }}
        >
          {row.groupName}
        </span>
      ),
    },
    {
      title: "Members",
      key: "members",
      width: 100,
      render: (_, row) => <MemberCell row={row} />,
    },
    {
      title: "Type",
      key: "type",
      width: 120,
      render: (_, row) => <ClassBadge tone={row.classification} />,
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      render: (_, row) => {
        if (row.policyExists) {
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 11.5,
                color: AN_COLORS.muted,
                fontFamily: AN_FONT,
                fontStyle: "italic",
              }}
            >
              <Check size={11} strokeWidth={2} /> Policy exists
            </span>
          );
        }
        const value = rowState[row.adGroupId]?.action || "mfa_required";
        return (
          <Select
            value={value}
            onChange={(v) => updateRow(row.adGroupId, { action: v })}
            options={ACTION_OPTIONS}
            style={{ width: 160 }}
            size="small"
            popupMatchSelectWidth={false}
            optionRender={(opt) => (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: ACTION_COLOR[opt.value] || AN_COLORS.muted,
                  }}
                />
                {opt.label}
              </span>
            )}
          />
        );
      },
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={680}
      title={
        <div>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 4,
            }}
          >
            Scan group classification · 1-click bulk apply
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: AN_COLORS.ink }}>
            Discover policies from AD
          </div>
        </div>
      }
      styles={{ body: { padding: "20px 22px" }, header: { padding: "18px 22px 14px" } }}
      footer={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: AN_COLORS.ink2,
            }}
          >
            {!scanLoading && suggestions.length > 0 && (
              <>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: AN_COLORS.success,
                  }}
                />
                <span>
                  Scan complete ·{" "}
                  <span style={{ color: AN_COLORS.muted }}>
                    {suggestions.length} groups found
                    {suggestions.filter((s) => s.policyExists).length > 0 &&
                      `, ${suggestions.filter((s) => s.policyExists).length} already have a policy`}
                  </span>
                </span>
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose} disabled={applyLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={applyLoading}
              onClick={handleApply}
              disabled={selectedCount === 0}
            >
              Apply selected ({selectedCount})
            </Button>
          </div>
        </div>
      }
    >
      {messageContext}

      {/* Domain picker / rescan strip */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          marginBottom: 18,
          padding: 14,
          background: AN_COLORS.bgWarm,
          borderRadius: 8,
          border: `1px solid ${AN_COLORS.line}`,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 500,
              color: AN_COLORS.ink2,
              marginBottom: 6,
            }}
          >
            AD domain <span style={{ color: AN_COLORS.muted, fontWeight: 400 }}>*</span>
          </div>
          <Select
            value={adId}
            onChange={handleDomainChange}
            showSearch
            placeholder={
              (domains || []).length === 0
                ? "No AD domains found"
                : "Search or pick a domain"
            }
            style={{ width: "100%" }}
            notFoundContent="No AD domains found"
            options={(domains || []).map((d) => ({
              value: d.id,
              label: d.directoryName || d.domainName,
            }))}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label || "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
        <Button
          type="primary"
          icon={<RefreshCw size={13} />}
          onClick={() => runScan()}
          loading={scanLoading}
          disabled={!adId}
        >
          {suggestions.length > 0 ? "Rescan" : "Scan"}
        </Button>
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 10,
          fontSize: 11.5,
          color: AN_COLORS.muted,
        }}
      >
        <LegendDot color="var(--an-danger)" /> Privileged
        <LegendDot color="#1E55C9" /> Standard
        <LegendDot color={AN_COLORS.muted} /> Service
        <span
          style={{
            marginLeft: "auto",
            fontFamily: AN_MONO,
            fontSize: 11,
          }}
        >
          {selectedCount} of {suggestions.length} selected
        </span>
      </div>

      {/* Suggestions table */}
      <div
        style={{
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 8,
          overflow: "hidden",
          background: "var(--an-surface)",
        }}
      >
        <Table
          rowKey="adGroupId"
          columns={columns}
          dataSource={suggestions}
          loading={scanLoading}
          pagination={false}
          size="small"
          locale={{
            emptyText: scanLoading
              ? "Scanning…"
              : suggestions.length === 0
              ? "Pick a domain above and click Scan to discover groups."
              : "No groups found. Ensure adsync has run at least once.",
          }}
          rowClassName={(row) => (row.policyExists ? "row-disabled" : "")}
        />
      </div>

      {/* Helper text */}
      <div
        style={{
          marginTop: 12,
          fontSize: 11.5,
          color: AN_COLORS.muted,
          lineHeight: 1.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 6,
        }}
      >
        <Info
          size={12}
          style={{ flexShrink: 0, marginTop: 3 }}
        />
        <span>
          Privileged groups are pre-selected with{" "}
          <span style={{ color: AN_COLORS.ink2, fontWeight: 500 }}>Require MFA</span>;
          service accounts are pre-set to{" "}
          <span style={{ color: AN_COLORS.ink2, fontWeight: 500 }}>Allow</span>. Override any
          row before applying.
        </span>
      </div>
    </Drawer>
  );
}

// The scan already returns the member names it sampled, so the count is a
// link into them rather than a number you have to go and verify elsewhere.
function MemberCell({ row }) {
  const count = row.memberCount ?? 0;
  const members = Array.isArray(row.members) ? row.members : [];
  const label = `${count} ${count === 1 ? "user" : "users"}`;
  const numberStyle = {
    fontFamily: AN_MONO,
    fontVariantNumeric: "tabular-nums",
    fontSize: 12.5,
  };

  if (members.length === 0) {
    return <span style={{ ...numberStyle, color: AN_COLORS.ink2 }}>{label}</span>;
  }

  const truncated = count > members.length || members.length >= MEMBER_SAMPLE_CAP;

  return (
    <Popover
      trigger="click"
      placement="right"
      title={
        <div style={{ fontSize: 12.5, fontWeight: 600, color: AN_COLORS.ink }}>
          {row.groupName}
        </div>
      }
      content={
        <div style={{ maxWidth: 260 }}>
          <div
            style={{
              maxHeight: 220,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {members.map((m) => (
              <span
                key={m}
                style={{ fontSize: 12, fontFamily: AN_MONO, color: AN_COLORS.ink2 }}
              >
                {m}
              </span>
            ))}
          </div>
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: `1px solid ${AN_COLORS.line}`,
              fontSize: 11,
              color: AN_COLORS.muted,
              lineHeight: 1.45,
            }}
          >
            {truncated
              ? `Sample of ${members.length} — the scan returns at most ${MEMBER_SAMPLE_CAP} names per group, out of ${count}.`
              : `Sample of ${members.length} name${
                  members.length === 1 ? "" : "s"
                } returned by the scan, out of ${count}.`}
          </div>
        </div>
      }
    >
      <button
        type="button"
        style={{
          ...numberStyle,
          border: "none",
          background: "transparent",
          padding: 0,
          color: AN_COLORS.primary,
          cursor: "pointer",
          textDecoration: "underline",
          textUnderlineOffset: 2,
        }}
      >
        {label}
      </button>
    </Popover>
  );
}

function LegendDot({ color }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span
        style={{ width: 7, height: 7, borderRadius: "50%", background: color }}
      />
    </span>
  );
}
